import { createSlice } from '@reduxjs/toolkit';

import { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { DefaultInitialState, SliceName } from '@/shared/config';

import {
  fetchRegistryAssets,
  FetchRegistryAssetsMode,
} from './registryAssetsSlice.thunks';

type RegistryAssetsState = DefaultInitialState & {
  byId: Record<string, RegistryAsset>;
  orderedIds: string[];
  total: number;
  availableLanguages: string[];
  loadingMode: FetchRegistryAssetsMode | null;
  error: string | null;
};

const initialState: RegistryAssetsState = {
  byId: {},
  orderedIds: [],
  total: 0,
  availableLanguages: [],
  loading: 'idle',
  loadingMode: null,
  error: null,
};

const registryAssetsSlice = createSlice({
  name: SliceName.registryAssets,
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRegistryAssets.pending, (state, action) => {
        state.loading = 'pending';
        state.loadingMode = action.meta.arg.mode ?? 'replace';
        state.error = null;
      })
      .addCase(fetchRegistryAssets.fulfilled, (state, action) => {
        const { items, total, availableLanguages } = action.payload;
        const mode = action.meta.arg.mode ?? 'replace';

        if (mode === 'replace') {
          state.byId = {};
          state.orderedIds = [];
        }

        items.forEach((item) => {
          if (!state.byId[item.id]) {
            state.orderedIds.push(item.id);
          }
          state.byId[item.id] = item;
        });

        state.total = total;
        state.availableLanguages = availableLanguages;
        state.loading = 'succeeded';
        state.loadingMode = null;
        state.error = null;
      })
      .addCase(fetchRegistryAssets.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.loading = 'failed';
        state.loadingMode = null;
        state.error = action.error.message ?? 'Failed to load registry assets';
      });
  },
});

export const { actions } = registryAssetsSlice;

export default registryAssetsSlice.reducer;
